import { TextStyle } from '@tiptap/extension-text-style'
import { Typography } from '@tiptap/extension-typography'
import { Placeholder, Selection } from '@tiptap/extensions'
import {
	type Editor,
	type Content,
	type UseEditorOptions,
	useEditor,
	useEditorState,
} from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import * as React from 'react'
import { toast } from 'sonner'
import { cn } from '#app/utils/misc'
import {
	Image,
	HorizontalRule,
	CodeBlockLowlight,
	Color,
	UnsetAllMarks,
	ResetMarksOnEnter,
	FileHandler,
} from '../extensions'
import { useThrottle } from '../hooks/use-throttle'
import { fileToBase64, getOutput, randomId } from '../utils'

export interface UseMinimalTiptapEditorProps extends UseEditorOptions {
	value?: Content
	output?: 'html' | 'json' | 'text'
	placeholder?: string
	editorClassName?: string
	throttleDelay?: number
	onUpdate?: (content: Content) => void
	onBlur?: (content: Content) => void
	uploader?: (file: File) => Promise<string>
}

async function fakeuploader(file: File): Promise<string> {
	// NOTE: This is a fake upload function. Replace this with your own upload logic.
	// This function should return the uploaded image URL.

	// wait 3s to simulate upload
	await new Promise((resolve) => setTimeout(resolve, 3000))

	const src = await fileToBase64(file)

	return src
}

const createExtensions = ({
	placeholder,
	uploader,
}: {
	placeholder: string
	uploader?: (file: File) => Promise<string>
}) => [
	StarterKit.configure({
		blockquote: { HTMLAttributes: { class: 'block-node' } },
		// bold
		bulletList: { HTMLAttributes: { class: 'list-node' } },
		code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
		codeBlock: false,
		// document
		dropcursor: { width: 2, class: 'ProseMirror-dropcursor border' },
		// gapcursor
		// hardBreak
		heading: { HTMLAttributes: { class: 'heading-node' } },
		// undoRedo
		horizontalRule: false,
		// italic
		// listItem
		// listKeymap
		link: {
			enableClickSelection: true,
			openOnClick: false,
			HTMLAttributes: {
				class: 'link',
			},
		},
		orderedList: { HTMLAttributes: { class: 'list-node' } },
		paragraph: { HTMLAttributes: { class: 'text-node' } },
		// strike
		// text
		// underline
		// trailingNode
	}),
	Image.configure({
		allowedMimeTypes: ['image/*'],
		maxFileSize: 5 * 1024 * 1024,
		allowBase64: true,
		uploadFn: async (file) => {
			return uploader ? await uploader(file) : await fakeuploader(file)
		},
		onToggle(editor, files, pos) {
			editor.commands.insertContentAt(
				pos,
				files.map((image) => {
					const blobUrl = URL.createObjectURL(image)
					const id = randomId()

					return {
						type: 'image',
						attrs: {
							id,
							src: blobUrl,
							fileName: image.name,
						},
					}
				}),
			)
		},
		onImageRemoved({ id: _id, src: _src }) {
			// Image removed callback - could be used for cleanup if needed
		},
		onValidationError(errors) {
			errors.forEach((error) => {
				toast.error('Image validation error', {
					position: 'bottom-right',
					description: error.reason,
				})
			})
		},
		onActionSuccess({ action }) {
			const mapping = {
				copyImage: 'Copy Image',
				copyLink: 'Copy Link',
				download: 'Download',
			}
			toast.success(mapping[action], {
				position: 'bottom-right',
				description: 'Image action success',
			})
		},
		onActionError(error, { action }) {
			const mapping = {
				copyImage: 'Copy Image',
				copyLink: 'Copy Link',
				download: 'Download',
			}
			toast.error(`Failed to ${mapping[action]}`, {
				position: 'bottom-right',
				description: error.message,
			})
		},
	}),
	FileHandler.configure({
		allowBase64: true,
		allowedMimeTypes: ['image/*'],
		maxFileSize: 5 * 1024 * 1024,
		onDrop: (editor, files, pos) => {
			files.forEach(async (file) => {
				const src = await fileToBase64(file)
				editor.commands.insertContentAt(pos, {
					type: 'image',
					attrs: { src },
				})
			})
		},
		onPaste: (editor, files) => {
			files.forEach(async (file) => {
				const src = await fileToBase64(file)
				editor.commands.insertContent({
					type: 'image',
					attrs: { src },
				})
			})
		},
		onValidationError: (errors) => {
			errors.forEach((error) => {
				toast.error('Image validation error', {
					position: 'bottom-right',
					description: error.reason,
				})
			})
		},
	}),
	Color,
	TextStyle,
	Selection,
	Typography,
	UnsetAllMarks,
	HorizontalRule,
	ResetMarksOnEnter,
	CodeBlockLowlight,
	Placeholder.configure({ placeholder: () => placeholder }),
]

export const useMinimalTiptapEditor = ({
	value,
	output = 'html',
	placeholder = '',
	editorClassName,
	throttleDelay = 0,
	onUpdate,
	onBlur,
	uploader,
	...props
}: UseMinimalTiptapEditorProps) => {
	const throttledSetValue = useThrottle(
		(value: Content) => onUpdate?.(value),
		throttleDelay,
	)

	const handleUpdate = React.useCallback(
		(editor: Editor) => {
			// Only call if we have an onUpdate handler - avoids unnecessary work
			if (onUpdate) {
				throttledSetValue(getOutput(editor, output))
			}
		},
		[output, throttledSetValue, onUpdate],
	)

	const handleCreate = React.useCallback((_editor: Editor) => {
		// Content is handled by TipTap's built-in content option
		// No manual content setting needed
	}, [])

	const handleBlur = React.useCallback(
		(editor: Editor) => {
			onBlur?.(getOutput(editor, output))
		},
		[output, onBlur],
	)

	const editor = useEditor({
		immediatelyRender: false,
		content: value, // Let TipTap handle initial content properly
		extensions: createExtensions({ placeholder, uploader }),
		editorProps: {
			attributes: {
				autocomplete: 'off',
				autocorrect: 'off',
				autocapitalize: 'off',
				class: cn('focus:outline-hidden', editorClassName),
			},
		},
		onUpdate: ({ editor }) => {
			handleUpdate(editor)
		},
		onCreate: ({ editor }) => {
			handleCreate(editor)
		},
		onBlur: ({ editor }) => {
			handleBlur(editor)
		},
		...props,
	})

	const { editor: mainEditor } = useEditorState({
		editor,
		selector(context) {
			if (!context.editor) {
				return {
					editor: null,
					editorState: undefined,
					canCommand: undefined,
				}
			}

			return {
				editor: context.editor,
				editorState: context.editor.state,
				canCommand: context.editor.can,
			}
		},
	})

	return mainEditor
}

export default useMinimalTiptapEditor
